import React, { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import * as XLSX from 'xlsx';
import {
    IndianRupee,
    Clock,
    CheckCircle,
    Download,
    RefreshCw,
    Eye,
    Search
} from 'lucide-react';
import { api } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import usePersistentFilters from '../../hooks/usePersistentFilters';

const GST = () => {
    const { hasPermission } = useAuth();
    const [regionalOffices, setRegionalOffices] = useState([]);
    const navigate = useNavigate();
    const params = useParams();

    const canView = hasPermission('financial', 'view');

    // Get source information from URL params
    const urlParams = new URLSearchParams(window.location.search);
    const sourceFrom = urlParams.get('from') || 'dashboard';
    const sourceId = urlParams.get('sourceId') || params.id;
    const originalSource = urlParams.get('originalSource') || 'financial';

    console.log('GST Page - Source params:', { sourceFrom, sourceId, originalSource });

    const defaultFilters = {
        startDate: '',
        endDate: '',
        regionalOffice: '',
        gstStatus: '',
        developerOnly: 'Exclude',
        search: '',
        page: 1,
        limit: 20
    };

    const {
        filters,
        handleFilterChange,
        handlePageChange,
        clearFilters
    } = usePersistentFilters(defaultFilters, 'gst-filters');

    // Fetch GST summary data
    const { data: summaryData, isLoading: summaryLoading, refetch: refetchSummary } = useQuery(
        ['gst-summary', filters],
        () => api.get('/gst/summary', {
            params: {
                startDate: filters.startDate,
                endDate: filters.endDate,
                regionalOffice: filters.regionalOffice,
                gstStatus: filters.gstStatus,
                developerOnly: filters.developerOnly,
                search: filters.search
            }
        }),
        {
            keepPreviousData: true,
            enabled: canView,
            staleTime: 30000, // 30 seconds
        }
    );

    // Helper function to check if filter is active and get CSS classes
    const getFilterClasses = (filterValue, isCheckbox = false, isSearch = false) => {
        // For developer filter, consider 'Exclude' as inactive (default state)
        const isActive = isCheckbox
            ? filterValue === true
            : (filterValue && filterValue !== '' && filterValue !== 'Exclude');

        const baseClasses = isCheckbox
            ? "h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            : isSearch
                ? "pl-10 pr-4 py-2 w-full border rounded-md focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                : "form-input";

        if (isActive) {
            return isCheckbox
                ? `${baseClasses} ring-2 ring-blue-400 bg-blue-50`
                : `${baseClasses} border-blue-500 bg-blue-50 shadow-md ring-1 ring-blue-200 transition-all duration-200`;
        }

        return isSearch
            ? `${baseClasses} border-gray-300`
            : baseClasses;
    };

    // Fetch GST report data
    const { data: reportData, isLoading: reportLoading, error } = useQuery(
        ['gst-report', filters],
        () => api.get('/gst/report', { params: filters }),
        {
            keepPreviousData: true,
            enabled: canView,
            retry: 1, // Reduce retries to fail faster
            staleTime: 30000 // Cache for 30 seconds
        }
    );

    // Fetch regional offices for filter dropdown
    useEffect(() => {
        const fetchRegionalOffices = async () => {
            if (!canView) return;
            try {
                const response = await api.get('/regional-offices', {
                    params: { limit: 100, isActive: true }
                });
                if (response.data.success) {
                    setRegionalOffices(response.data.data.offices || []);
                }
            } catch (error) {
                console.warn('Failed to fetch regional offices:', error);
            }
        };

        if (canView) {
            fetchRegionalOffices();
        }
    }, [canView]);

    if (error) {
        toast.error('Failed to load GST data');
    }

    const summary = summaryData?.data?.data || {
        totalGST: { total: 0, collected: 0, pending: 0 },
        payments: { total: 0, completed: 0, pending: 0 },
        fareAmount: { total: 0, completed: 0, pending: 0 }
    };

    // Helper function to safely format numbers
    const safeFormat = (value) => {
        const num = value || 0;
        return typeof num === 'number' ? num : 0;
    };

    // Export functionality
    const handleExportToExcel = async () => {
        try {
            toast.loading('Preparing export...', { id: 'export' });

            // Get all filtered data for export (remove pagination limit but keep filters)
            const exportParams = {
                ...filters,
                limit: 10000, // Get all filtered records
                page: 1
            };

            const exportResponse = await api.get('/gst/report', { params: exportParams });
            const exportData = exportResponse?.data?.data?.payments || [];

            if (exportData.length === 0) {
                toast.error('No data available for export with current filters', { id: 'export' });
                return;
            }

            toast.loading('Fetching driver details...', { id: 'export' });

            // Get unique driver IDs
            const driverIds = [...new Set(exportData.map(payment => {
                // Handle different driver ID structures
                return payment.driverId?._id ||
                    payment.driverId?.id ||
                    payment.driverId ||
                    payment.driver?._id;
            }).filter(Boolean))];

            let driversMap = {};

            // Fetch driver details using the ids parameter
            if (driverIds.length > 0) {
                try {
                    const driversResponse = await api.get('/drivers', {
                        params: {
                            ids: driverIds.join(','),
                            limit: 1000
                        }
                    });

                    if (driversResponse?.data?.success) {
                        const drivers = driversResponse.data.data.drivers || [];
                        drivers.forEach(driver => {
                            // Store driver name using different possible ID formats
                            driversMap[driver._id] = driver.name || 'Unknown Driver';
                            if (driver.id) driversMap[driver.id] = driver.name || 'Unknown Driver';
                        });
                    }
                } catch (error) {
                    console.warn('Failed to fetch drivers:', error);
                    // Continue with export even if driver fetch fails
                }
            }

            toast.loading('Generating Excel file...', { id: 'export' });

            // Prepare data for Excel export
            const excelData = exportData.map((payment, index) => {
                // Use GST amounts calculated by backend
                const gstAmount = payment.gstAmount || 0;
                const cgstAmount = payment.cgstAmount || 0;
                const sgstAmount = payment.sgstAmount || 0;
                
                // Format invoice generated date
                const formatDate = (timestamp) => {
                    if (!timestamp) return 'N/A';
                    const date = new Date(timestamp);
                    return date.toLocaleDateString('en-IN', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric'
                    });
                };

                // Get driver ID for lookup
                const driverIdForLookup = payment.driverId?._id ||
                    payment.driverId?.id ||
                    payment.driverId ||
                    payment.driver?._id;

                // Get GST status
                const gstStatusText = payment.gstStatus === 'completed' ? 'Paid' : 'Pending';

                return {
                    'S.No': index + 1,
                    'Driver Name': driversMap[driverIdForLookup] ||
                        payment.driverId?.name ||
                        payment.driver?.name ||
                        payment.driverName ||
                        'Driver Not Found',
                    'Trip ID': payment.tripId || 'N/A',
                    'Invoice ID': payment.invoiceId || 'N/A',
                    'Invoice Generated Date': formatDate(payment.invoiceGeneratedDate || payment.createdAt),
                    'CGST (₹)': cgstAmount.toFixed(2),
                    'SGST (₹)': sgstAmount.toFixed(2),
                    'Total GST (₹)': gstAmount.toFixed(2),
                    'GST Status': gstStatusText,
                    // 'Created Date': payment.createdAt ? new Date(payment.createdAt).toLocaleDateString() : 'N/A'
                };
            });

            // Create workbook and worksheet
            const workbook = XLSX.utils.book_new();
            const worksheet = XLSX.utils.json_to_sheet(excelData);

            // Set column widths
            const colWidths = [
                { wch: 8 },  // S.No
                { wch: 20 }, // Driver Name
                { wch: 25 }, // Trip ID
                { wch: 20 }, // Invoice ID
                { wch: 18 }, // Invoice Generated Date
                { wch: 12 }, // CGST
                { wch: 12 }, // SGST
                { wch: 12 }, // Total GST
                { wch: 15 }, // GST Status
            ];
            worksheet['!cols'] = colWidths;

            // Add worksheet to workbook
            XLSX.utils.book_append_sheet(workbook, worksheet, 'GST Report');

            // Generate filename with current date and filter info
            const currentDate = new Date().toISOString().split('T')[0];
            let filename = `GST_Report_${currentDate}`;

            // Add filter info to filename if filters are applied
            if (filters.startDate || filters.endDate || filters.regionalOffice || filters.gstStatus) {
                filename += '_filtered';
            }
            filename += '.xlsx';

            // Download the file
            XLSX.writeFile(workbook, filename);

            toast.success(`Exported ${excelData.length} filtered records successfully!`, { id: 'export' });

        } catch (error) {
            console.error('Export failed:', error);
            toast.error('Failed to export data. Please try again.', { id: 'export' });
        }
    };

    const handleDriverClick = (driverId) => {
        const currentParams = new URLSearchParams({
            from: 'gst-details',
            sourceId: driverId,
            originalSource: sourceFrom
        });
        navigate(`/drivers/${driverId}?${currentParams.toString()}`);
    };

    const handleTripClick = (tripId) => {
        const currentParams = new URLSearchParams({
            from: 'gst-details',
            sourceId: tripId,
            originalSource: sourceFrom
        });
        navigate(`/trips/${tripId}?${currentParams.toString()}`);
    };

    const handleBackNavigation = () => {
        console.log('GST Back Navigation - sourceFrom:', sourceFrom, 'sourceId:', sourceId, 'originalSource:', originalSource);
        
        if (sourceFrom === 'dashboard') {
            navigate('/dashboard');
        } else if (sourceFrom === 'financial') {
            navigate('/financial');
        } else if (sourceFrom === 'reports') {
            navigate('/reports');
        } else if (sourceFrom === 'due-details' && sourceId) {
            // Navigate back to regional office due details
            navigate(`/financial/due/office/${sourceId}`);
        } else if (sourceFrom === 'driver-details' && sourceId) {
            // Navigate back to driver details
            navigate(`/drivers/${sourceId}`);
        } else if (sourceFrom === 'trip-details' && sourceId) {
            // Navigate back to trip details
            navigate(`/trips/${sourceId}`);
        } else {
            // Fallback to financial dashboard
            navigate('/financial');
        }
    };

    if (!canView) {
        return (
            <div className="empty-state">
                <IndianRupee className="empty-state-icon" />
                <h3 className="empty-state-title">Access Denied</h3>
                <p className="empty-state-description">
                    You don't have permission to view GST reports.
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Page Header */}
            <div className="page-header">
                <div className="px-4 py-5 sm:px-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="page-title">GST Management</h1>
                            <p className="page-subtitle">
                                Monitor and manage GST collections from ride payments
                            </p>
                        </div>
                        <div className="flex items-center space-x-3">
                            {(sourceFrom !== 'dashboard' && sourceFrom !== 'direct') && (
                                <button
                                    onClick={handleBackNavigation}
                                    className="btn btn-outline btn-sm"
                                >
                                    ← Back to {sourceFrom === 'financial' ? 'Financial' : sourceFrom === 'reports' ? 'Reports' : 'Previous Page'}
                                </button>
                            )}
                            <button
                                onClick={refetchSummary}
                                className="btn btn-outline btn-sm"
                                disabled={summaryLoading}
                            >
                                <RefreshCw className={`h-4 w-4 mr-2 ${summaryLoading ? 'animate-spin' : ''}`} />
                                Refresh
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* GST Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Total GST Card */}
                <div className="card">
                    <div className="card-body p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600 mb-1">Total GST</p>
                                <p className="text-3xl font-bold text-gray-900">
                                    {summaryLoading ? '...' : `₹${safeFormat(summary.totalGST.total).toLocaleString()}`}
                                </p>
                                <p className="text-sm text-gray-500 mt-1">
                                    From {safeFormat(summary.payments.total).toLocaleString()} payments
                                </p>
                            </div>
                            <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center">
                                <IndianRupee className="h-6 w-6 text-white" />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Collected GST Card */}
                <div className="card">
                    <div className="card-body p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600 mb-1">Collected GST</p>
                                <p className="text-3xl font-bold text-green-600">
                                    {summaryLoading ? '...' : `₹${safeFormat(summary.totalGST.collected).toLocaleString()}`}
                                </p>
                                <p className="text-sm text-gray-500 mt-1">
                                    From {safeFormat(summary.payments.completed).toLocaleString()} completed payments
                                </p>
                            </div>
                            <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center">
                                <CheckCircle className="h-6 w-6 text-white" />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Pending GST Card */}
                <div className="card">
                    <div className="card-body p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600 mb-1">Pending GST</p>
                                <p className="text-3xl font-bold text-orange-600">
                                    {summaryLoading ? '...' : `₹${safeFormat(summary.totalGST.pending).toLocaleString()}`}
                                </p>
                                <p className="text-sm text-gray-500 mt-1">
                                    From {safeFormat(summary.payments.pending).toLocaleString()} pending payments
                                </p>
                            </div>
                            <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center">
                                <Clock className="h-6 w-6 text-white" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Filters Section */}
            <div className="card">
                <div className="card-body p-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                            <span className="text-xl font-medium ">Filters</span>
                        </div>
                        <div className="flex space-x-2 mt-2">
                            <button
                                onClick={clearFilters}
                                className="btn btn-outline btn-sm"
                            >
                                Clear Filters
                            </button>
                        </div>
                    </div>
                    <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
                         <div >
                            <label className="form-label">Search</label>
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-3 w-3 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Search by driver name, invoice ID"
                                    value={filters.search}
                                    onChange={(e) => handleFilterChange('search', e.target.value)}
                                    className={getFilterClasses(filters.search, false, true)}
                                />
                            </div>
                        </div>
                        <div>
                            <label className="form-label">Start Date</label>
                            <input
                                type="date"
                                value={filters.startDate}
                                onChange={(e) => handleFilterChange('startDate', e.target.value)}
                                className={getFilterClasses(filters.startDate)}
                            />
                        </div>
                        <div>
                            <label className="form-label">End Date</label>
                            <input
                                type="date"
                                value={filters.endDate}
                                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                                className={getFilterClasses(filters.endDate)}
                            />
                        </div>
                        <div>
                            <label className="form-label">
                                Regional Office
                            </label>
                            <select
                                value={filters.regionalOffice}
                                onChange={(e) => handleFilterChange('regionalOffice', e.target.value)}
                                className={getFilterClasses(filters.regionalOffice)}
                            >
                                <option value="">All Offices</option>
                                {regionalOffices.map(office => (
                                    <option key={office._id} value={office._id}>
                                        {office.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="form-label">
                                GST Status
                            </label>
                            <select
                                value={filters.gstStatus}
                                onChange={(e) => handleFilterChange('gstStatus', e.target.value)}
                                className={getFilterClasses(filters.gstStatus)}
                            >
                                <option value="">All Status</option>
                                <option value="completed">Paid</option>
                                <option value="pending">Pending</option>
                            </select>
                        </div>

                        <div>
                            <label className="form-label">Developer Filter</label>
                            <select
                                value={filters.developerOnly}
                                onChange={(e) => handleFilterChange('developerOnly', e.target.value)}
                                className={getFilterClasses(filters.developerOnly)}
                            >
                                <option value="Exclude">Exclude Developers</option>
                                <option value="Include">Include Developers</option>
                                <option value="Only">Developers Only</option>
                            </select>
                        </div>
                    </div>
                </div>
            </div>

            {/* GST Report Table */}
            <div className="card">
                <div className="card-header">
                    <div className="flex items-center justify-between">
                        <h3 className="text-lg font-medium text-gray-900">
                            GST Report ({reportData?.data?.data?.pagination?.totalRecords || 0})
                        </h3>
                        <button
                            onClick={handleExportToExcel}
                            className="btn btn-outline"
                            disabled={reportLoading || !reportData?.data?.data?.payments?.length}
                        >
                            <Download className="h-4 w-4 mr-2" />
                            Export
                        </button>
                    </div>
                </div>
                <div className="card-body">
                    {reportLoading ? (
                        <div className="flex items-center justify-center py-8">
                            <div className="loading-spinner"></div>
                        </div>
                    ) : (!reportData?.data?.data?.payments || reportData.data.data.payments.length === 0) ? (
                        <div className="empty-state">
                            <IndianRupee className="empty-state-icon" />
                            <h3 className="empty-state-title">No GST records found</h3>
                            <p className="empty-state-description">
                                No GST records match your current filters.
                            </p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="table">
                                <thead className="table-header">
                                    <tr>
                                        <th className="table-header-cell">Invoice ID</th>
                                        <th className="table-header-cell">Driver</th>
                                        <th className="table-header-cell">Invoice Generated Date</th>
                                        <th className="table-header-cell">CGST (₹)</th>
                                        <th className="table-header-cell">SGST (₹)</th>
                                        <th className="table-header-cell">Total GST (₹)</th>
                                        <th className="table-header-cell">Status</th>
                                        <th className="table-header-cell">Action</th>
                                    </tr>
                                </thead>
                                <tbody className="table-body">
                                    {reportData?.data?.data?.payments?.map((payment) => {
                                        // Use GST amounts calculated by backend
                                        const gstAmount = payment.gstAmount || 0;
                                        const cgstAmount = payment.cgstAmount || 0;
                                        const sgstAmount = payment.sgstAmount || 0;
                                        
                                        // Format invoice generated date
                                        const formatDate = (timestamp) => {
                                            if (!timestamp) return 'N/A';
                                            const date = new Date(timestamp);
                                            return date.toLocaleDateString('en-IN', {
                                                day: '2-digit',
                                                month: '2-digit',
                                                year: 'numeric'
                                            });
                                        };

                                        return (
                                            <tr key={payment._id} className="table-row">
                                                <td className="table-cell">
                                                    <div className="text-sm font-medium text-gray-900">
                                                        {payment.invoiceId || 'N/A'}
                                                    </div>
                                                </td>
                                                <td className="table-cell">
                                                    {payment.driverId ? (
                                                        <button
                                                            onClick={() => handleDriverClick(
                                                                payment.driverId?._id ||
                                                                payment.driverId?.id ||
                                                                payment.driverId
                                                            )}
                                                            className="text-sm font-medium text-blue-600 hover:text-blue-900 hover:underline"
                                                            title="View Driver Details"
                                                        >
                                                            {payment.driverId?.name ||
                                                                payment.driver?.name ||
                                                                payment.driverName ||
                                                                'Driver'}
                                                        </button>
                                                    ) : (
                                                        <span className="text-sm text-gray-500">N/A</span>
                                                    )}
                                                </td>
                                                <td className="table-cell">
                                                    <div className="text-sm text-gray-900">
                                                        {formatDate(payment.invoiceGeneratedDate || payment.createdAt)}
                                                    </div>
                                                </td>
                                                <td className="table-cell">
                                                    <div className="text-sm text-gray-900">
                                                        ₹{cgstAmount.toFixed(2)}
                                                    </div>
                                                </td>
                                                <td className="table-cell">
                                                    <div className="text-sm text-gray-900">
                                                        ₹{sgstAmount.toFixed(2)}
                                                    </div>
                                                </td>
                                                <td className="table-cell">
                                                    <div className="text-sm text-gray-900">
                                                        ₹{gstAmount.toFixed(2)}
                                                    </div>
                                                </td>
                                                <td className="table-cell">
                                                    {payment.gstStatus ? (
                                                        <span
                                                            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${payment.gstStatus === 'completed'
                                                                ? 'bg-green-100 text-green-800'
                                                                : payment.gstStatus === 'partial'
                                                                    ? 'bg-orange-100 text-orange-800'
                                                                    : 'bg-yellow-100 text-yellow-800'
                                                                }`}
                                                        >
                                                            {payment.gstStatus === 'completed' ? 'PAID' : 
                                                             payment.gstStatus === 'pending' ? 'PENDING' : 'PENDING'}
                                                        </span>
                                                    ) : payment.passengerPaymentStatus ? (
                                                        <span
                                                            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${payment.passengerPaymentStatus === 'completed'
                                                                ? 'bg-green-100 text-green-800'
                                                                : payment.passengerPaymentStatus === 'pending'
                                                                    ? 'bg-yellow-100 text-yellow-800'
                                                                    : 'bg-red-100 text-red-800'
                                                                }`}
                                                        >
                                                            {payment.passengerPaymentStatus?.toUpperCase()}
                                                        </span>
                                                    ) : (
                                                        <span className="text-sm text-gray-500">N/A</span>
                                                    )}
                                                </td>
                                                <td className="table-cell">
                                                    <div className="flex items-center space-x-2">
                                                        <button
                                                            onClick={() => handleTripClick(payment.tripId)}
                                                            className="text-primary-600 hover:text-primary-900"
                                                            title="View Trip Details"
                                                        >
                                                            <Eye className="h-4 w-4" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    }) || []}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Pagination */}
            {reportData?.data?.data?.payments && reportData.data.data.payments.length > 0 && (
                <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-700">
                        Showing {((reportData.data.data.pagination.current - 1) * reportData.data.data.pagination.limit) + 1} to{' '}
                        {Math.min(((reportData.data.data.pagination.current - 1) * reportData.data.data.pagination.limit) + reportData.data.data.payments.length, reportData.data.data.pagination.totalRecords)} of{' '}
                        {reportData.data.data.pagination.totalRecords} results
                    </div>
                    {reportData.data.data.pagination.total > 1 && (
                        <div className="flex space-x-2">
                            <button
                                onClick={() => handlePageChange(reportData.data.data.pagination.current - 1)}
                                disabled={reportData.data.data.pagination.current === 1}
                                className="btn btn-outline btn-sm"
                            >
                                Previous
                            </button>
                            <button
                                onClick={() => handlePageChange(reportData.data.data.pagination.current + 1)}
                                disabled={reportData.data.data.pagination.current === reportData.data.data.pagination.total}
                                className="btn btn-outline btn-sm"
                            >
                                Next
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default GST;